import React, { useEffect, useState } from "react";
import { Terminal, Server, Wifi, ExternalLink, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";

const CodeBlock = ({ code, lang = "bash", testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative my-4" data-testid={testid}>
      <div className="flex items-center justify-between bg-[#13161C] border border-[#272E3A] border-b-0 px-4 py-2">
        <span className="font-mono text-xs uppercase tracking-widest text-[#94A3B8]">{lang}</span>
        <button
          onClick={copy}
          className="font-mono text-xs text-[#94A3B8] hover:text-[#FF5500] flex items-center gap-1.5 transition-colors"
          data-testid={`${testid}-copy`}
        >
          {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <pre className="bg-[#0A0C10] border border-[#272E3A] p-4 overflow-x-auto">
        <code className="font-mono text-sm text-slate-200 whitespace-pre">{code}</code>
      </pre>
    </div>
  );
};

export default function MQTT() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/mqtt/config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const mctomqttCfg = cfg
    ? `# /etc/mctomqtt/config.d/15-corecomms.net.toml

[[broker]]
name = "CoreComms"
enabled = true
server = "mqtt.corecomms.net"
port = 443
transport = "websockets"
keepalive = 60
qos = 0
retain = true

[broker.tls]
enabled = true
verify = true

[broker.auth]
method = "token"
audience = "mqtt.corecomms.net"`
    : "";

  const mcpcCfg = cfg
    ? `# /root/.meshcore-packet-capture/.env.local

# MQTT Broker 3 - CoreComms
PACKETCAPTURE_MQTT3_ENABLED=true
PACKETCAPTURE_MQTT3_SERVER=mqtt.corecomms.net
PACKETCAPTURE_MQTT3_PORT=443
PACKETCAPTURE_MQTT3_TRANSPORT=websockets
PACKETCAPTURE_MQTT3_USE_TLS=true
PACKETCAPTURE_MQTT3_USE_AUTH_TOKEN=true
PACKETCAPTURE_MQTT3_TOKEN_AUDIENCE=mqtt.corecomms.net
PACKETCAPTURE_MQTT3_KEEPALIVE=120

# MQTT Topics for Broker 3 - Default Pattern
PACKETCAPTURE_MQTT3_TOPIC_STATUS=meshcore/{IATA}/{PUBLIC_KEY}/status
PACKETCAPTURE_MQTT3_TOPIC_PACKETS=meshcore/{IATA}/{PUBLIC_KEY}/packets`
    : "";

  const mosquittoTest = cfg
    ? `# Test broker connectivity
mosquitto_sub -h ${cfg.host} -p ${cfg.port} \\
  -u "${cfg.username}" -P "${cfg.password}" \\
  -t "${cfg.topic_root}/#" -v`
    : "";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="mqtt-page">
      <div className="overline mb-3">/mqtt — observer configuration</div>
      <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
        Observer to MQTT
      </h1>
      <p className="text-lg text-slate-300 max-w-3xl leading-relaxed mb-10">
        Observer nodes are MeshCore radios that are either attached to a small Linux host (Raspberry Pi, Mini PC), or standalone radios with built-in MQTT support that publish
        heard packets to centralized MQTT brokers such as MeshTrace.net and LetsMesh!. These observers are crucial for helping the community understand the total health of the mesh, as well as providing insight into how packets are flowing through the network. This page will guide you through setting up your observers to connect to MQTT brokers, which are then consumed by analyzers and visualizers.
      </p>

      {/* Broker info */}
      <div className="bg-[#13161C] border border-[#272E3A] mb-10" data-testid="mqtt-broker-card">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#272E3A]">
          <Server className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-lg">Broker connection</h2>
          <span className="ml-auto inline-flex items-center gap-2 font-mono text-xs text-[#10B981]">
            <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse-dot" /> ONLINE
          </span>
        </div>
        <dl className="divide-y divide-[#272E3A] font-mono">
          {cfg && [
            { k: "Host", v: cfg.host },
            { k: "Audience", v: cfg.host },
            { k: "WebSocket port", v: String(cfg.websocket_port) },
            { k: "TLS", v: String(cfg.tls) },
            { k: "Authentication", v: String(cfg.auth) },
            { k: "Topic root", v: cfg.topic_root }
          ].map((r) => (
            <div key={r.k} className="px-6 py-3 grid grid-cols-2 gap-4">
              <dt className="text-xs uppercase tracking-widest text-[#94A3B8]">{r.k}</dt>
              <dd className="text-sm text-white text-right break-all">{r.v}</dd>
            </div>
          ))}
        </dl>
        <div className="px-6 py-4 border-t border-[#272E3A] text-xs text-[#94A3B8]">
          If your region would like mqtt viewer access for your own projects, reach out to an admin on discord.
        </div>
      </div>

      <h1 id="standalone" class="font-display font-black text-white text-2xl sm:text-3xl lg:text-4xl tracking-tighter mb-6">Standalone Observers</h1>
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Wifi className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-2xl">1. Flashing your device</h2>
        </div>
        <p className="text-slate-300 mb-4">
          The standalone observer is a LoRa radio image that has the MQTT configuration pre-baked in. This is the easiest way to get up and running, but it only works for certain hardware types. If you have a LoRa radio with built in wifi, this is the recommended onboarding method.
        </p>
        <p class="mt-3">
          Don't have a radio but would like to help? Click a hardware device below (We suggest the Heltec V4) to purchase one and follow the instructions below.
        </p>
        <div class="bg-[#13161C] border border-[#272E3A] mb-10 mt-10" data-testid="mqtt-devices-card">
          <dl className="divide-y divide-[#272E3A] font-mono">
            <div class="flex items-center gap-3 px-6 py-4 border-b border-[#272E3A]">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-cpu w-6 h-6 text-[#FF5500]" aria-hidden="true">
                <path d="M12 20v2"></path>
                <path d="M12 2v2"></path>
                <path d="M17 20v2"></path>
                <path d="M17 2v2"></path>
                <path d="M2 12h2"></path>
                <path d="M2 17h2"></path>
                <path d="M2 7h2"></path>
                <path d="M20 12h2"></path>
                <path d="M20 17h2"></path>
                <path d="M20 7h2"></path>
                <path d="M7 20v2"></path>
                <path d="M7 2v2"></path>f
                <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                <rect x="8" y="8" width="8" height="8" rx="1"></rect>
              </svg>
              <h2 class="font-display font-bold text-white text-lg">Supported Hardware</h2>
            </div>
            <div className="px-6 py-3 grid grid-cols-2 gap-4">
              <dt className="text-xs uppercase tracking-widest text-[#FF5500]"><a href="https://a.co/d/0373meF5" target="_blank" data-discover="true">Heltec V3</a></dt>
              <dt className="text-xs uppercase tracking-widest text-[#FF5500]"><a href="https://a.co/d/02uYiHnv" target="_blank" data-discover="true">Heltec V4 (Recommended) *</a></dt>
              <dt className="text-xs uppercase tracking-widest text-[#FF5500]"><a href="https://shop.uniteng.com/product/meshtastic-mesh-device-station-edition/" target="_blank" data-discover="true">Station G2 *</a></dt>
              <dt className="text-xs uppercase tracking-widest text-[#94A3B8]">Tbeam SX1262</dt>
              <dt className="text-xs uppercase tracking-widest text-[#94A3B8]">Tbeam SX1276 *</dt>
              <dt className="text-xs uppercase tracking-widest text-[#FF5500]"><a href="https://store.rokland.com/products/lilygo-t-beam-supreme-esp32-s3-lora-development-board-sx1262-915mhz-gps-l76k-or-u-blox?gad_source=1&gad_campaignid=21531368556&gbraid=0AAAAAD8Vrr7_-TagZXr6pjKNt13-3cXwb&gclid=Cj0KCQjwzqXQBhD2ARIsAKrIeU_zdRAgp0F6lCTwSzVm2XXB_N0UZwOBDCNBM2tZ3e5TRxI4GnXYpFQaAjWpEALw_wcB" target="_blank" data-discover="true">TBeam S3 Supreme SX1262 *</a></dt>
              <dt className="text-xs uppercase tracking-widest text-[#FF5500]"><a href="https://www.seeedstudio.com/Wio-SX1262-with-XIAO-ESP32S3-p-5982.html?srsltid=AfmBOoqO_sv4WJQc5F1Liz714bETXER7fFlSrKJVJ-ufHnslQFYtu22u" target="_blank" data-discover="true">Xiao S3 WIO *</a></dt>
            </div>
          </dl>
        </div>
        <p class="mt-3">
          <a
            href="https://observer.gessaman.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ecmc-builds-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold transition-colors whitespace-nowrap">
            Flash your device <ExternalLink className="w-4 h-4" />
          </a>
        </p>
        <div class="bg-[#0B0E14] border border-[#272E3A] border-l-2 border-l-[#FF5500] p-6 mt-4">
          <div className="overline mb-2">IMPORTANT</div>
          <p className="text-slate-300">
            The hardware list above have an * annotation for devices that contain <b>PSRAM</b>. To have 6 concurrent wss/ssl mqtt connections, you need a device with PSRAM. If you choose a device that doesn't, we will provide a config that will allow you to push data to us and letsmesh-US
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-display font-bold text-white text-2xl mb-4">2. Configuring</h2>
        <p class="mt-3">
          Next, connect to the device in the Meshcore app and manage it as you would any other repeater or room server with the default password, <b>password</b>. Select <b>Command Line</b> at the bottom and enter the commands below, populating your own values.
        </p>
        <h2 className="font-display font-bold text-white text-1xl mb-4 mt-4">For devices with PSRAM</h2>
        <CodeBlock testid="config-code-psram" type="console" code={"set name ObserverName\nset wifi.ssid SSID\nset wifi.pwd PASSWORD\nset timezone America/New_York\nset mqtt3.preset corecomms\nset path.hash.mode 1\nset mqtt.iata YourIATA\nset mqtt.tx on\nreboot"} />
        <h2 className="font-display font-bold text-white text-1xl mb-4">For devices with without PSRAM</h2>
        <CodeBlock testid="config-code" type="console" code={"set name ObserverName\nset wifi.ssid SSID\nset wifi.pwd PASSWORD\nset timezone America/New_York\nset mqtt2.preset corecomms\nset path.hash.mode 1\nset mqtt.iata YourIATA\nset mqtt.tx on\nreboot"} />
      </section>
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-2xl">3. Verify you're up by checking node statuses</h2>
        </div>
        <a
          href="https://east-us.corecomms.net/nodes/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="letsmesh-ostatus-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold transition-colors whitespace-nowrap">
          Node Status <ExternalLink className="w-4 h-4" />
        </a>
      </section>


      <h1 id="connected" class="font-display font-black text-white text-2xl sm:text-3xl lg:text-4xl tracking-tighter mb-6">Connected Observers</h1>
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Wifi className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-2xl">1. Install the observer service</h2>
        </div>
        <p className="text-slate-300 mb-4">
          Letsmesh.net is your best source for making a PC attached observer. They have a full onboarding process and have simplified the process based on what hardware/firmware type you'd like to make an observer with.
          Visit their onboard page.
        </p>
        <p>
          <a
            href="https://analyzer.letsmesh.net/observer/onboard"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="letsmesh-onboarding-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold transition-colors whitespace-nowrap">
            Observer Obnboarding <ExternalLink className="w-4 h-4" />
          </a>
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-display font-bold text-white text-2xl mb-4">2. Configure</h2>
        <p className="text-slate-300 mb-4">Locate the configuration file for the onboarding you chose.</p>
        <h3 className="font-display font-bold text-slate-400 mb-4">Repeaters // RoomServers · MeshcoreToMQTT</h3>
        <CodeBlock testid="config-code" code={mctomqttCfg || "Loading..."} lang="yaml" />
        <h3 className="font-display font-bold text-slate-400 mb-4">Companions · Meshcore Packet Capture</h3>
        <CodeBlock testid="config-code" code={mcpcCfg || "Loading..."} lang="yaml" />
      </section>

      <section className="mb-12">
        <h2 className="font-display font-bold text-white text-2xl mb-4">3. Restart the service</h2>
        <h3 className="font-display font-bold text-slate-400 mb-4">Repeaters // RoomServers · MeshcoreToMQTT</h3>
        <CodeBlock testid="config-code" code="systemctl restart mctomqtt" lang="yaml" />
        <h3 className="font-display font-bold text-slate-400 mb-4">Companions · Meshcore Packet Capture</h3>
        <CodeBlock testid="config-code" code="systemctl restart meshcore-capture" lang="yaml" />
      </section>

      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-2xl">4. Verify you're up by checking observer statuses</h2>
        </div>
        <a
          href="https://east-us.corecomms.net/nodes/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="letsmesh-ostatus-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold transition-colors whitespace-nowrap">
          Node Status <ExternalLink className="w-4 h-4" />
        </a>
      </section>

      <div className="bg-[#0B0E14] border border-[#272E3A] border-l-2 border-l-[#FF5500] p-6">
        <div className="overline mb-2">Need help?</div>
        <p className="text-slate-300">
          Open <a href="https://discord.gg/3TPXsHBv6ts" target="_blank" rel="noopener noreferrer" className="text-[#FF5500] hover:text-[#FF7326] underline underline-offset-4 inline-flex items-center gap-1">#observers on Discord <ExternalLink className="w-3 h-3" /></a> — operators are usually around.
        </p>
      </div>
    </div>
  );
}
